/*
  Allure Studios — Global JS
  Site-wide interactions: mobile menu, cart drawer, search drawer, quick add,
  favorites (localStorage), accordions, reveal-on-scroll, back-to-top,
  announcement bar rotation, and shop-the-look hotspots.
  No external dependencies. Progressive enhancement — native <form> and
  Shopify routes keep working with JS disabled.
*/
(function () {
  "use strict";

  window.Allure = window.Allure || {};
  var routes = window.Allure.routes || {
    cartAdd: "/cart/add.js",
    cartChange: "/cart/change.js",
    cartUpdate: "/cart/update.js",
    cartGet: "/cart.js",
  };

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------------------------------------------------
     Helpers
  ------------------------------------------------------------ */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function trapFocus(container, initialFocus) {
    var focusable = qsa(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      container
    );
    if (!focusable.length) return function () {};
    var first = focusable[0], last = focusable[focusable.length - 1];
    (initialFocus || first).focus();

    function handleKeydown(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
    container.addEventListener("keydown", handleKeydown);
    return function () { container.removeEventListener("keydown", handleKeydown); };
  }

  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || (window.Allure.moneyFormat || "${{amount}}");

    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split(".");
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : "";
      return dollars + cents2;
    }
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount": value = formatWithDelimiters(cents, 2); break;
      case "amount_no_decimals": value = formatWithDelimiters(cents, 0); break;
      case "amount_with_comma_separator": value = formatWithDelimiters(cents, 2, ".", ","); break;
      case "amount_no_decimals_with_comma_separator": value = formatWithDelimiters(cents, 0, ".", ","); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }
  window.Allure.formatMoney = formatMoney;

  /* ---------------------------------------------------------
     Reveal on scroll
  ------------------------------------------------------------ */
  function initReveal() {
    var items = qsa(".reveal");
    if (!items.length) return;
    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      items.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------
     Back to top
  ------------------------------------------------------------ */
  function initBackToTop() {
    var btn = qs("[data-back-to-top]");
    if (!btn) return;
    window.addEventListener(
      "scroll",
      debounce(function () {
        btn.classList.toggle("is-visible", window.scrollY > 900);
      }, 100)
    );
    btn.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
    });
  }

  /* ---------------------------------------------------------
     Announcement bar rotation
  ------------------------------------------------------------ */
  function initAnnouncementBar() {
    var bar = qs("[data-announcement-bar]");
    if (!bar) return;
    var items = qsa(".announcement-bar__item", bar);
    if (items.length < 2) return;
    var interval = parseInt(bar.getAttribute("data-interval"), 10) || 5000;
    var i = 0;
    setInterval(function () {
      items[i].classList.remove("is-active");
      i = (i + 1) % items.length;
      items[i].classList.add("is-active");
    }, interval);
  }

  /* ---------------------------------------------------------
     Accordions (generic)
  ------------------------------------------------------------ */
  function setPanelHeight(panel, open) {
    if (open) {
      panel.style.maxHeight = panel.scrollHeight + "px";
    } else {
      panel.style.maxHeight = 0;
    }
  }

  function initAccordions() {
    document.addEventListener("click", function (e) {
      var trigger = e.target.closest(".accordion__trigger, .mobile-menu__group-toggle");
      if (!trigger) return;
      var expanded = trigger.getAttribute("aria-expanded") === "true";
      var panelId = trigger.getAttribute("aria-controls");
      var panel = panelId ? document.getElementById(panelId) : trigger.nextElementSibling;
      if (!panel) return;

      // Accordion groups that should only allow one open at a time
      var group = trigger.closest("[data-accordion-group]");
      if (group && !expanded) {
        qsa(".accordion__trigger", group).forEach(function (t) {
          if (t !== trigger) {
            t.setAttribute("aria-expanded", "false");
            var pid = t.getAttribute("aria-controls");
            var p = pid ? document.getElementById(pid) : t.nextElementSibling;
            if (p) setPanelHeight(p, false);
          }
        });
      }

      trigger.setAttribute("aria-expanded", String(!expanded));
      setPanelHeight(panel, !expanded);
    });

    // Recalculate open panel heights on resize (copy for wrapping text)
    window.addEventListener(
      "resize",
      debounce(function () {
        qsa('.accordion__trigger[aria-expanded="true"], .mobile-menu__group-toggle[aria-expanded="true"]').forEach(
          function (t) {
            var pid = t.getAttribute("aria-controls");
            var p = pid ? document.getElementById(pid) : t.nextElementSibling;
            if (p) setPanelHeight(p, true);
          }
        );
      }, 150)
    );
  }

  /* ---------------------------------------------------------
     Mobile menu
  ------------------------------------------------------------ */
  function initMobileMenu() {
    var menu = qs("[data-mobile-menu]");
    var openBtn = qs("[data-mobile-menu-open]");
    var closeBtn = qs("[data-mobile-menu-close]");
    if (!menu || !openBtn) return;
    var releaseFocus;

    function open() {
      menu.classList.add("is-open");
      document.documentElement.style.overflow = "hidden";
      openBtn.setAttribute("aria-expanded", "true");
      releaseFocus = trapFocus(menu.querySelector(".mobile-menu__panel"));
    }
    function close() {
      menu.classList.remove("is-open");
      document.documentElement.style.overflow = "";
      openBtn.setAttribute("aria-expanded", "false");
      if (releaseFocus) releaseFocus();
      openBtn.focus();
    }
    openBtn.addEventListener("click", open);
    if (closeBtn) closeBtn.addEventListener("click", close);
    qs(".mobile-menu__overlay", menu) && qs(".mobile-menu__overlay", menu).addEventListener("click", close);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && menu.classList.contains("is-open")) close();
    });
  }

  /* ---------------------------------------------------------
     Generic drawer controller (cart + search)
  ------------------------------------------------------------ */
  function DrawerController(root) {
    this.root = root;
    this.releaseFocus = null;
    var self = this;
    qsa(".drawer__overlay, [data-drawer-close]", root).forEach(function (el) {
      el.addEventListener("click", function () { self.close(); });
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && root.classList.contains("is-open")) self.close();
    });
  }
  DrawerController.prototype.open = function (focusEl) {
    this.root.classList.add("is-open");
    document.documentElement.style.overflow = "hidden";
    this.releaseFocus = trapFocus(qs(".drawer__panel", this.root), focusEl);
    this.root.dispatchEvent(new CustomEvent("drawer:open"));
  };
  DrawerController.prototype.close = function () {
    this.root.classList.remove("is-open");
    document.documentElement.style.overflow = "";
    if (this.releaseFocus) this.releaseFocus();
  };

  var cartDrawerEl = qs("[data-cart-drawer]");
  var searchDrawerEl = qs("[data-search-drawer]");
  window.Allure.cartDrawer = cartDrawerEl ? new DrawerController(cartDrawerEl) : null;
  window.Allure.searchDrawer = searchDrawerEl ? new DrawerController(searchDrawerEl) : null;

  function initDrawerTriggers() {
    qsa("[data-cart-drawer-open]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        if (window.Allure.cartDrawer) {
          e.preventDefault();
          window.Allure.cartDrawer.open();
        }
      });
    });
    qsa("[data-search-drawer-open]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        if (window.Allure.searchDrawer) {
          e.preventDefault();
          window.Allure.searchDrawer.open(qs('input[type="search"]', searchDrawerEl));
        }
      });
    });
  }

  /* ---------------------------------------------------------
     Cart (Ajax API)
  ------------------------------------------------------------ */
  var Cart = {
    lineTemplate: function (item) {
      var img = item.image
        ? '<img class="cart-line__image" src="' + item.image.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, "_240x300$1$2") + '" alt="' + (item.product_title || "") + '" width="88" height="110" loading="lazy">'
        : '<div class="cart-line__image"></div>';
      var variant = item.variant_title ? '<div class="cart-line__variant">' + item.variant_title + '</div>' : "";
      return (
        '<div class="cart-line" data-cart-line data-key="' + item.key + '">' +
          '<a href="' + item.url + '">' + img + "</a>" +
          '<div class="cart-line__details">' +
            '<a href="' + item.url + '" class="cart-line__title">' + item.product_title + "</a>" +
            variant +
            '<div class="cart-line__qty" data-qty-selector>' +
              '<button type="button" data-qty-decrease aria-label="Decrease quantity">&minus;</button>' +
              '<input type="number" min="0" value="' + item.quantity + '" data-qty-input aria-label="Quantity">' +
              '<button type="button" data-qty-increase aria-label="Increase quantity">+</button>' +
            "</div>" +
            '<button type="button" class="cart-line__remove" data-cart-remove>Remove</button>' +
          "</div>" +
          '<div class="cart-line__price">' + formatMoney(item.final_line_price) + "</div>" +
        "</div>"
      );
    },

    render: function (cart) {
      var countEls = qsa("[data-cart-count]");
      countEls.forEach(function (el) {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });

      // Drawer
      var drawerBody = qs("[data-cart-drawer-body]");
      var drawerFooter = qs("[data-cart-drawer-footer]");
      if (drawerBody) {
        if (cart.item_count === 0) {
          drawerBody.innerHTML =
            '<div class="cart-empty"><p class="h4">Your bag is empty.</p>' +
            '<p class="text-muted mt-1">Elevated essentials are waiting.</p>' +
            '<a href="/collections/the-first-drop" class="btn mt-3">Shop The First Drop</a></div>';
          if (drawerFooter) drawerFooter.hidden = true;
        } else {
          if (drawerFooter) drawerFooter.hidden = false;
          drawerBody.innerHTML = cart.items.map(Cart.lineTemplate).join("");
        }
      }
      var drawerSubtotal = qs("[data-cart-subtotal]");
      if (drawerSubtotal) drawerSubtotal.textContent = formatMoney(cart.total_price);

      // Standalone cart page (/cart)
      var pageBody = qs("[data-cart-page-body]");
      if (pageBody) {
        var pageEmpty = qs("[data-cart-page-empty]");
        var pageForm = qs("[data-cart-page-form]");
        if (cart.item_count === 0) {
          if (pageEmpty) pageEmpty.hidden = false;
          if (pageForm) pageForm.hidden = true;
        } else {
          if (pageEmpty) pageEmpty.hidden = true;
          if (pageForm) pageForm.hidden = false;
          pageBody.innerHTML = cart.items.map(Cart.lineTemplate).join("");
        }
        var pageSubtotal = qs("[data-cart-page-subtotal]");
        if (pageSubtotal) pageSubtotal.textContent = formatMoney(cart.total_price);
      }
    },

    refresh: function () {
      return fetch(routes.cartGet)
        .then(function (r) { return r.json(); })
        .then(function (cart) { Cart.render(cart); return cart; });
    },

    add: function (formData, button) {
      if (button) { button.classList.add("btn--loading"); button.disabled = true; }
      return fetch(routes.cartAdd, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) {
            throw new Error(data.description || "Could not add item to bag");
          }
          return Cart.refresh().then(function () {
            if (window.Allure.cartDrawer) window.Allure.cartDrawer.open();
          });
        })
        .catch(function (err) {
          document.dispatchEvent(new CustomEvent("cart:error", { detail: err }));
        })
        .finally(function () {
          if (button) { button.classList.remove("btn--loading"); button.disabled = false; }
        });
    },

    change: function (key, quantity) {
      return fetch(routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) { Cart.render(cart); return cart; });
    },
  };
  window.Allure.Cart = Cart;

  function initCartInteractions() {
    // Add-to-cart forms (PDP + quick add) — progressive enhancement
    document.addEventListener("submit", function (e) {
      var form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      var formData = new FormData(form);
      var submitBtn = qs('[type="submit"]', form);
      Cart.add(formData, submitBtn);
    });

    // Quantity controls inside cart drawer
    document.addEventListener("click", function (e) {
      var decrease = e.target.closest("[data-qty-decrease]");
      var increase = e.target.closest("[data-qty-increase]");
      var remove = e.target.closest("[data-cart-remove]");
      if (!decrease && !increase && !remove) return;
      var line = e.target.closest("[data-cart-line]");
      if (!line) return;
      var key = line.getAttribute("data-key");
      var input = qs("[data-qty-input]", line);
      var qty = parseInt(input.value, 10) || 1;
      if (increase) qty += 1;
      if (decrease) qty = Math.max(0, qty - 1);
      if (remove) qty = 0;
      line.style.opacity = "0.5";
      Cart.change(key, qty);
    });

    document.addEventListener(
      "change",
      function (e) {
        var input = e.target.closest("[data-qty-input]");
        if (!input) return;
        var line = input.closest("[data-cart-line]");
        var key = line.getAttribute("data-key");
        Cart.change(key, Math.max(0, parseInt(input.value, 10) || 0));
      },
      true
    );
  }

  /* ---------------------------------------------------------
     Quick Add modal
  ------------------------------------------------------------ */
  var QuickAdd = {
    modal: null,
    init: function () {
      this.modal = qs("[data-quick-add-modal]");
      if (!this.modal) return;
      var self = this;
      document.addEventListener("click", function (e) {
        var trigger = e.target.closest("[data-quick-add-trigger]");
        if (trigger) {
          e.preventDefault();
          self.open(trigger.getAttribute("data-product-handle"));
        }
        if (e.target.closest("[data-quick-add-close]") || e.target.classList.contains("quick-add-modal__overlay")) {
          self.close();
        }
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") self.close();
      });
    },
    open: function (handle) {
      var self = this;
      fetch("/products/" + handle + ".js")
        .then(function (r) { return r.json(); })
        .then(function (product) { self.render(product); self.modal.classList.add("is-open"); document.documentElement.style.overflow = "hidden"; })
        .catch(function () {});
    },
    close: function () {
      this.modal.classList.remove("is-open");
      document.documentElement.style.overflow = "";
    },
    render: function (product) {
      var container = qs("[data-quick-add-content]", this.modal);
      if (!container) return;
      var variants = product.variants;
      var img = product.featured_image || (product.images && product.images[0]) || "";
      var optionsHtml = product.options
        .map(function (name, idx) {
          var values = [];
          variants.forEach(function (v) {
            var val = v["option" + (idx + 1)];
            if (val && values.indexOf(val) === -1) values.push(val);
          });
          var buttons = values
            .map(function (val, i) {
              return (
                '<button type="button" class="size-option' + (i === 0 ? " is-selected" : "") + '" data-option-index="' + idx + '" data-value="' + val + '">' + val + "</button>"
              );
            })
            .join("");
          return (
            '<div class="variant-picker" data-option-group="' + idx + '">' +
              '<div class="variant-picker__label">' + name + "</div>" +
              '<div class="size-list">' + buttons + "</div>" +
            "</div>"
          );
        })
        .join("");

      container.innerHTML =
        '<div class="quick-add-modal__media"><img src="' + img + '" alt="' + product.title + '" loading="lazy"></div>' +
        '<div class="quick-add-modal__info">' +
          '<button class="modal-close" data-quick-add-close aria-label="Close">&times;</button>' +
          '<p class="eyebrow">' + (product.vendor || "Allure Studios") + "</p>" +
          '<h3 class="h3 mt-1">' + product.title + "</h3>" +
          '<p class="product-info__price mt-1">' + formatMoney(variants[0].price) + "</p>" +
          '<form data-product-form data-quick-add-form>' +
            '<input type="hidden" name="id" value="' + variants[0].id + '">' +
            optionsHtml +
            '<button type="submit" class="btn btn--full mt-3">Add To Bag</button>' +
          "</form>" +
          '<a href="' + product.url + '" class="link-quiet body-sm mt-2" style="display:inline-block;">View full details</a>' +
        "</div>";

      var form = qs("[data-quick-add-form]", container);
      var selected = {};
      product.options.forEach(function (name, idx) { selected[idx] = variants[0]["option" + (idx + 1)]; });

      function updateSelection() {
        var match = variants.find(function (v) {
          return product.options.every(function (name, idx) { return v["option" + (idx + 1)] === selected[idx]; });
        });
        if (match) {
          qs('input[name="id"]', form).value = match.id;
          qs(".product-info__price", container).textContent = formatMoney(match.price);
        }
      }

      container.addEventListener("click", function (e) {
        var btn = e.target.closest(".size-option");
        if (!btn) return;
        var group = btn.closest("[data-option-group]");
        qsa(".size-option", group).forEach(function (b) { b.classList.remove("is-selected"); });
        btn.classList.add("is-selected");
        selected[btn.getAttribute("data-option-index")] = btn.getAttribute("data-value");
        updateSelection();
      });
    },
  };

  /* ---------------------------------------------------------
     Favorites (localStorage — per-viewer, no server sync)
  ------------------------------------------------------------ */
  var Favorites = {
    key: "allure_favorites",
    get: function () {
      try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (e) { return []; }
    },
    set: function (list) {
      try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) {}
    },
    toggle: function (id) {
      var list = this.get();
      var idx = list.indexOf(id);
      if (idx > -1) list.splice(idx, 1);
      else list.push(id);
      this.set(list);
      return idx === -1;
    },
    has: function (id) { return this.get().indexOf(id) > -1; },
    paint: function () {
      var list = this.get();
      qsa("[data-favorite-toggle]").forEach(function (btn) {
        var id = btn.getAttribute("data-product-id");
        btn.classList.toggle("is-active", list.indexOf(id) > -1);
      });
    },
  };
  window.Allure.Favorites = Favorites;

  function initFavorites() {
    Favorites.paint();
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-favorite-toggle]");
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute("data-product-id");
      var isFav = Favorites.toggle(id);
      qsa('[data-favorite-toggle][data-product-id="' + id + '"]').forEach(function (b) {
        b.classList.toggle("is-active", isFav);
        b.setAttribute("aria-pressed", String(isFav));
      });
    });
  }

  /* ---------------------------------------------------------
     Shop the Look hotspots
  ------------------------------------------------------------ */
  function initHotspots() {
    qsa(".shop-the-look").forEach(function (section) {
      document.addEventListener("click", function (e) {
        var hotspot = e.target.closest(".hotspot");
        if (hotspot && section.contains(hotspot)) {
          e.stopPropagation();
          var popover = document.getElementById(hotspot.getAttribute("aria-controls"));
          qsa(".hotspot-popover", section).forEach(function (p) {
            if (p !== popover) p.classList.remove("is-open");
          });
          if (popover) popover.classList.toggle("is-open");
        } else if (!e.target.closest(".hotspot-popover")) {
          qsa(".hotspot-popover", section).forEach(function (p) { p.classList.remove("is-open"); });
        }
      });
    });
  }

  /* ---------------------------------------------------------
     Newsletter (native Shopify /contact#newsletter form, progressive AJAX)
  ------------------------------------------------------------ */
  function initNewsletterForms() {
    qsa("[data-newsletter-form-wrap] form").forEach(function (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var status = qs("[data-form-status]", form);
        var formData = new FormData(form);
        fetch(form.action, { method: "POST", body: formData, headers: { Accept: "application/json" } })
          .then(function () {
            form.reset();
            if (status) {
              status.textContent = "You're on the list — welcome to the club.";
              status.classList.add("form-status--success");
            }
          })
          .catch(function () {
            if (status) {
              status.textContent = "Something went wrong. Please try again.";
              status.classList.add("form-status--error");
            }
          });
      });
    });
  }

  /* ---------------------------------------------------------
     Init
  ------------------------------------------------------------ */
  document.addEventListener("DOMContentLoaded", function () {
    initReveal();
    initBackToTop();
    initAnnouncementBar();
    initAccordions();
    initMobileMenu();
    initDrawerTriggers();
    initCartInteractions();
    QuickAdd.init();
    initFavorites();
    initHotspots();
    initNewsletterForms();
  });
})();
